"use strict";

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useMemo } from "react";

import CharacterSelector, {
  getCharacterKey,
  getCharacterLabel,
} from "./CharacterSelector.js";
import VoicePreviewRow from "./VoicePreviewRow.js";
import useVoiceCreation, {
  EMOTION_OPTIONS,
  TEXT_MAX_LENGTH,
  VOICE_DESCRIPTION_MAX_LENGTH,
} from "./useVoiceCreation.js";

const VoiceCreationSection = ({
  title,
  endpoint,
  projectId,
  projectName,
  characters,
  allowMultipleCharacters,
  defaultVoiceDescription = "Make this voice have a Mexican Accent",
  selectedCharacterKeys,
  onSelectedCharacterKeysChange,
  onCharacterRenamed,
}) => {
  const sortedCharacters = useMemo(() => {
    return [...characters].sort((a, b) => {
      const nameA = getCharacterLabel(a);
      const nameB = getCharacterLabel(b);
      return nameA.localeCompare(nameB);
    });
  }, [characters]);

  const selectableCharacterKeys = useMemo(() => {
    return sortedCharacters.map((character, index) =>
      getCharacterKey(character, index)
    );
  }, [sortedCharacters]);

  useEffect(() => {
    const validKeys = selectedCharacterKeys.filter((key) =>
      selectableCharacterKeys.includes(key)
    );

    if (validKeys.length !== selectedCharacterKeys.length) {
      onSelectedCharacterKeysChange(validKeys);
    }
  }, [selectableCharacterKeys, selectedCharacterKeys, onSelectedCharacterKeysChange]);

  const selectedCharacters = useMemo(() => {
    return sortedCharacters.filter((character, index) =>
      selectedCharacterKeys.includes(getCharacterKey(character, index))
    );
  }, [sortedCharacters, selectedCharacterKeys]);

  const getCharacterBySpeakerId = (speakerId) => {
    return sortedCharacters.find((character) => character.speaker === speakerId);
  };

  const {
    selectedEmotion,
    setSelectedEmotion,
    voiceDescription,
    setVoiceDescription,
    text,
    setText,
    autoGenerateText,
    setAutoGenerateText,
    loudness,
    setLoudness,
    guidanceScale,
    setGuidanceScale,
    promptStrengthPreset,
    setPromptStrengthPreset,
    voiceResults,
    acceptedVoiceIds,
    acceptingVoiceIds,
    isSubmitting,
    canSubmit,
    handleGenerateVoices,
    handleAcceptPreview,
  } = useVoiceCreation({
    title,
    endpoint,
    projectId,
    projectName,
    selectedCharacters,
    allowMultipleCharacters,
    defaultVoiceDescription,
  });

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        border: "1px solid rgba(223, 230, 238, 0.95)",
        borderRadius: "26px",
        backgroundColor: "rgba(255,255,255,0.96)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: "1px solid #edf0f5",
          background:
            "linear-gradient(135deg, rgba(31,111,150,0.08), rgba(255,255,255,0.96))",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 850,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid #edf0f5",
          backgroundColor: "#ffffff",
        }}
      >
        <CharacterSelector
          projectId={projectId}
          characters={sortedCharacters}
          selectedKeys={selectedCharacterKeys}
          onSelectionChange={onSelectedCharacterKeysChange}
          allowMultiple={allowMultipleCharacters}
          onCharacterRenamed={onCharacterRenamed}
        />
      </Box>

      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.75,
          borderBottom: "1px solid #edf0f5",
          backgroundColor: "#ffffff",
        }}
        spacing={2}
      >
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedEmotion}
              onChange={(event) => setSelectedEmotion(event.target.value)}
              sx={{
                borderRadius: "12px",
                minHeight: 44,
                backgroundColor: "#f9fafb",
                fontWeight: 650,
              }}
            >
              {EMOTION_OPTIONS.map((emotion) => (
                <MenuItem key={emotion} value={emotion}>
                  {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <Select
              value={promptStrengthPreset}
              onChange={(event) =>
                setPromptStrengthPreset(event.target.value)
              }
              sx={{
                borderRadius: "12px",
                minHeight: 44,
                backgroundColor: "#f9fafb",
                fontWeight: 650,
              }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2.5,
          borderBottom: "1px solid #edf0f5",
          backgroundColor: "#ffffff",
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          value={voiceDescription}
          onChange={(event) => setVoiceDescription(event.target.value)}
          placeholder="Make this voice have a Mexican Accent"
          inputProps={{ maxLength: VOICE_DESCRIPTION_MAX_LENGTH }}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: "#fbfcfe",
              alignItems: "flex-start",
            },
            "& textarea": {
              fontSize: 17,
              lineHeight: 1.55,
              color: "#111827",
            },
            "& textarea::placeholder": {
              color: "#777c89",
              opacity: 1,
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 0.75,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color:
                voiceDescription.length >= VOICE_DESCRIPTION_MAX_LENGTH
                  ? "error.main"
                  : "#8a94a6",
              fontWeight: 650,
            }}
          >
            {voiceDescription.length} / {VOICE_DESCRIPTION_MAX_LENGTH}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mx: { xs: 2, md: 3 },
          my: 2.5,
          p: { xs: 2, md: 2.5 },
          border: "1px solid #edf0f5",
          borderRadius: "20px",
          backgroundColor: "#fbfcfe",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
          Voice generation controls
        </Typography>

        <Grid container spacing={2.25}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoGenerateText}
                  onChange={(event) =>
                    setAutoGenerateText(event.target.checked)
                  }
                />
              }
              label="Auto-generate preview text"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Preview text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              disabled={autoGenerateText}
              inputProps={{ maxLength: TEXT_MAX_LENGTH }}
              helperText={
                autoGenerateText
                  ? "Text will be generated automatically."
                  : `${text.length} / ${TEXT_MAX_LENGTH}. Required; enter 100-1000 characters.`
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: autoGenerateText ? "#f3f4f6" : "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Loudness: {loudness}
            </Typography>
            <Slider
              value={loudness}
              min={-1}
              max={1}
              step={0.1}
              onChange={(event, value) => setLoudness(Number(value))}
              sx={{
                color: "#1f6f96",
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Guidance Scale: {guidanceScale}
            </Typography>
            <Slider
              value={guidanceScale}
              min={0}
              max={100}
              step={1}
              onChange={(event, value) => setGuidanceScale(Number(value))}
              sx={{
                color: "#1f6f96",
              }}
            />
          </Grid>
{/* 
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Seed"
              type="number"
              value={seed}
              onChange={(event) => setSeed(event.target.value)}
              inputProps={{
                min: 0,
                max: 2147483647,
                step: 1,
              }}
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Remixing Session ID"
              value={remixingSessionId}
              onChange={(event) => setRemixingSessionId(event.target.value)}
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Session Iteration ID"
              value={remixingSessionIterationId}
              onChange={(event) =>
                setRemixingSessionIterationId(event.target.value)
              }
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={streamPreviews}
                  onChange={(event) => setStreamPreviews(event.target.checked)}
                />
              }
              label="Stream previews instead of returning audio_base_64"
            />

            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6b7280" }}
            >
              Keep this off when you want the audio preview rows to be playable
              directly from the API response.
            </Typography>
          </Grid> */}
        </Grid>
      </Box>

      {voiceResults.length > 0 && (
        <Box
          sx={{
            mx: { xs: 2, md: 3 },
            mb: 2,
            border: "1px solid #e5e7eb",
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          {voiceResults.map((result, resultIndex) => {
            const resultSpeakerId =
              result.parent_speaker_id ||
              selectedCharacters[resultIndex]?.speaker ||
              selectedCharacters[0]?.speaker ||
              "";

            const resultCharacter = getCharacterBySpeakerId(resultSpeakerId);

            const resultCharacterName = resultCharacter
              ? getCharacterLabel(resultCharacter)
              : resultSpeakerId || `Character ${resultIndex + 1}`;

            const resultEmotion = result.selected_emotion || selectedEmotion;

            const previews = Array.isArray(result.previews)
              ? result.previews
              : [];

            return (
              <Box
                key={`${resultSpeakerId || "voice"}-${resultIndex}`}
                sx={{
                  borderTop:
                    resultIndex === 0 ? "none" : "1px solid #d1d5db",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: "#fafbfc",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {resultCharacterName}
                  </Typography>

                  {!!result.text && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6b7280",
                        lineHeight: 1.5,
                      }}
                    >
                      {result.text}
                    </Typography>
                  )}
                </Box>

                {previews.map((preview, previewIndex) => (
                  <VoicePreviewRow
                    key={`${
                      preview.generated_voice_id ||
                      `${resultSpeakerId}-${previewIndex}`
                    }`}
                    preview={preview}
                    characterName={resultCharacterName}
                    index={previewIndex}
                    speakerId={resultSpeakerId}
                    emotion={resultEmotion}
                    isAccepted={acceptedVoiceIds.includes(
                      preview.generated_voice_id
                    )}
                    isAccepting={acceptingVoiceIds.includes(
                      preview.generated_voice_id
                    )}
                    onAccept={handleAcceptPreview}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderTop: "1px solid #edf0f5",
          display: "flex",
          justifyContent: "flex-end",
          backgroundColor: "#ffffff",
        }}
      >
        <Button
          onClick={handleGenerateVoices}
          disabled={!canSubmit}
          aria-busy={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} thickness={5} sx={{ color: "white" }} />
            ) : null
          }
          sx={{
            height: 44,
            px: 3,
            borderRadius: 999,
            color: "white",
            fontWeight: 600,
            textTransform: "none",
            background:
              canSubmit
                ? "linear-gradient(135deg, #1f6f96, #2f8fb7)"
                : "#cfd6df",
            boxShadow: canSubmit
              ? "0 10px 22px rgba(31, 111, 150, 0.28)"
              : "none",
            transition: "all 160ms ease",
            "&:hover": {
              background:
                canSubmit
                  ? "linear-gradient(135deg, #195b7c, #277fa5)"
                  : "#cfd6df",
              transform: canSubmit ? "translateY(-1px)" : "none",
            },
            "&.Mui-disabled": {
              color: "white",
              background: isSubmitting
                ? "linear-gradient(135deg, #1f6f96, #2f8fb7)"
                : "#cfd6df",
            },
          }}
        >
          {isSubmitting ? "Generating Voices..." : "Generate Voices"}
        </Button>
      </Box>
    </Box>
  );
};

export default VoiceCreationSection;
